import React, { useEffect, useState } from "react";
import { ButtonLabel } from "../style/Table.style";
import { getVisibilityLabel } from "../../hooks/getVisibilityLabel";
import { ToggleSwitch } from "./ToggleSwitch";
import ArrowDownIcon from "src/img/arrow-down-sign-to-navigate.svg";
import {
    DirectoryExpandButton,
    DirectoryExpandIcon,
    DirectoryNameCellContent,
    DirectoryTableCell,
    DirectoryTableCenterCell,
    DirectoryTableRow,
    EmptyDirectoryTableCell
} from "../style/SubCategoriesTable.style";

import {
    getDirectoryLevel,
    getDirectoryLevelOption,
    getDirectoryParentName,
    getDirectoryPath,
    getParentSubcategoryUuid
} from "../../directoryLevels";

export const ManageSubCategoriesRow = ({
    tableData,
    getContentCount,
    manageSubCategories,
    onShow,
    isItemShow,
    categories,
    handleDragStart,
    handleDragEnter,
    handleDragEnd,
    handleDragOver,
    draggingIndex,
    hoveredIndex,
}) => {
    const [expandedCategories, setExpandedCategories] = useState([]);

    useEffect(() => {
        setExpandedCategories([]);
    }, [tableData]);

    const toggleCategory = uuid => {
        setExpandedCategories(current =>
            current.includes(uuid) ? current.filter(item => item !== uuid) : [...current, uuid]
        );
    };

    const hasExpandableChildren = item =>
        getDirectoryLevel(item) === "subcategory" &&
        tableData.some(row => getParentSubcategoryUuid(row) === item.uuid);

    const getChildRows = parentUuid =>
        tableData.filter(row => getParentSubcategoryUuid(row) === parentUuid);

    const buildVisibleRows = () => {
        const visibleRows = [];
        const subcategories = tableData.filter(item => getDirectoryLevel(item) === "subcategory");

        subcategories.forEach(subcategory => {
            visibleRows.push(subcategory);

            if (!expandedCategories.includes(subcategory.uuid)) return;

            getChildRows(subcategory.uuid).forEach(topic => {
                visibleRows.push(topic);
                getChildRows(topic.uuid).forEach(subtopic => {
                    visibleRows.push(subtopic);
                });
            });
        });

        return visibleRows;
    };

    const renderRow = (item, index) => {
        const directoryLevel = getDirectoryLevel(item);
        const isChildRow = directoryLevel !== "subcategory";
        const isDragging = index === draggingIndex;
        const isHovered = index === hoveredIndex;
        const isExpanded = expandedCategories.includes(item.uuid);

        return (
            <DirectoryTableRow
                key={index}
                className={isDragging ? "dragging" : ""}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragEnter={() => handleDragEnter(index)}
                onDragEnd={handleDragEnd}
                onDragOver={() => handleDragOver(index)}
                $isDragging={isDragging}
                $isChildRow={isChildRow}
            >
                <DirectoryTableCell $isHovered={isHovered} $isDragging={isDragging} $isChildRow={isChildRow} $wrap $width="200px">
                    <DirectoryNameCellContent>
                        <span>{item.categoryName}</span>
                        {hasExpandableChildren(item) && (
                            <DirectoryExpandButton
                                type="button"
                                onClick={() => toggleCategory(item.uuid)}
                                aria-label={isExpanded ? "Collapse" : "Expand"}
                            >
                                <DirectoryExpandIcon src={ArrowDownIcon} alt="" aria-hidden="true" $isExpanded={isExpanded} />
                            </DirectoryExpandButton>
                        )}
                    </DirectoryNameCellContent>
                </DirectoryTableCell>
                <DirectoryTableCell $isHovered={isHovered} $isDragging={isDragging} $isChildRow={isChildRow} $width="100px">
                    {getDirectoryLevelOption(directoryLevel).label}
                </DirectoryTableCell>
                <DirectoryTableCell $isHovered={isHovered} $isDragging={isDragging} $isChildRow={isChildRow} $width="100px">
                    {item?.body_and_mind_category?.name ?? "N/A"}
                </DirectoryTableCell>
                <DirectoryTableCell $isHovered={isHovered} $isDragging={isDragging} $isChildRow={isChildRow} $width="120px">
                    {getDirectoryParentName(item, tableData, categories) || "N/A"}
                </DirectoryTableCell>
                <DirectoryTableCell $isHovered={isHovered} $isDragging={isDragging} $isChildRow={isChildRow} $wrap $width="240px">
                    {getDirectoryPath(item, tableData, categories)}
                </DirectoryTableCell>
                <DirectoryTableCenterCell $isHovered={isHovered} $isDragging={isDragging} $isChildRow={isChildRow} $width="100px">
                    {getContentCount(item)}
                </DirectoryTableCenterCell>
                <DirectoryTableCell $isHovered={isHovered} $isDragging={isDragging} $isChildRow={isChildRow} $wrap>
                    {getVisibilityLabel(item.visibleTo)}
                </DirectoryTableCell>
                <DirectoryTableCenterCell $isHovered={isHovered} $isDragging={isDragging} $isChildRow={isChildRow}>
                    <ButtonLabel
                        onClick={() => {
                            manageSubCategories.toggleEditSubcategory(item);
                            manageSubCategories.setSubcategoryToUpdate(item);
                        }}
                    >
                        View/Edit
                    </ButtonLabel>
                </DirectoryTableCenterCell>
                <DirectoryTableCenterCell $isHovered={isHovered} $isDragging={isDragging} $isChildRow={isChildRow}>
                    <ToggleSwitch onChange={() => onShow(item)} checked={!isItemShow(item.uuid)} />
                </DirectoryTableCenterCell>
            </DirectoryTableRow>
        );
    };

    const renderEmpty = visibleRows => {
        const emptyRows = 7 - visibleRows.length;
        if (emptyRows < 1) return null;

        return Array.from({ length: emptyRows }, (_, i) => (
            <tr key={"empty_" + i}>
                {Array.from({ length: 9 }, (_, j) => (
                    <EmptyDirectoryTableCell key={`empty_cell_${i}_${j}`}>
                        &nbsp;
                    </EmptyDirectoryTableCell>
                ))}
            </tr>
        ));
    };

    const visibleRows = buildVisibleRows();

    return (
        <>
            {visibleRows.map(item => renderRow(item, tableData.indexOf(item)))}
            {renderEmpty(visibleRows)}
        </>
    );
};
